// Phase 7 R4 — atomic return recording via record_parts_return RPC.
// Updates PO line/header status, mirrors parts_lines.procurement_status,
// writes qty_returned audit events. Pricing is NEVER mutated.
// PO header never auto-closes — terminal R4 state is partially_returned.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RecordPartsReturnLineInput {
  purchase_order_line_id: string;
  quantity_returned: number;
  unit_credit?: number | null;
  reason?: string | null;
}

export interface RecordPartsReturnInput {
  shopId: string;
  purchaseOrderId: string;
  return_reference?: string | null;
  reason?: string | null;
  lines: RecordPartsReturnLineInput[];
}

export function useRecordPartsReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPartsReturnInput): Promise<string> => {
      if (!input.lines.length) throw new Error('Add at least one return line');

      const { data, error } = await (supabase as any).rpc('record_parts_return', {
        p_shop_id: input.shopId,
        p_purchase_order_id: input.purchaseOrderId,
        p_return: {
          return_reference: input.return_reference ?? null,
          reason: input.reason ?? null,
          lines: input.lines.map((l) => ({
            purchase_order_line_id: l.purchase_order_line_id,
            quantity_returned: l.quantity_returned,
            unit_credit: l.unit_credit ?? null,
            reason: l.reason ?? null,
          })),
        },
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-order', vars.purchaseOrderId] });
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['parts-lines'] });
      qc.invalidateQueries({ queryKey: ['ticket-po-linked-parts-lines'] });
      qc.invalidateQueries({ queryKey: ['po-returns', vars.purchaseOrderId] });
      qc.invalidateQueries({ queryKey: ['po-receipts', vars.purchaseOrderId] });
    },
  });
}
